import React, { useState, useMemo } from 'react';
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps';
import { getRegionConfig } from '../constants/mapRegions';
import MapPatternDefs from './MapPatternDefs';
import { getCountryId, getCountryVote, getCountryName, resolveVoteKey } from '../utils/geoCodes';

const DEFAULT_FILL = '#334155';

function getMemeOverride(isoA3, memeOverrides, countryVotes) {
  const voteKey = resolveVoteKey(isoA3, countryVotes);
  if (Object.prototype.hasOwnProperty.call(memeOverrides, voteKey)) {
    return memeOverrides[voteKey];
  }
  if (Object.prototype.hasOwnProperty.call(memeOverrides, isoA3)) {
    return memeOverrides[isoA3];
  }
  return null;
}

function getGeoFill(isoA3, mapData, { isMemeMode, memeOverrides }) {
  let vote = getCountryVote(isoA3, mapData.countryVotes);

  if (isMemeMode) {
    const override = getMemeOverride(isoA3, memeOverrides, mapData.countryVotes);
    if (override === 'A' || override === 'B') {
      vote = override;
    }
  }

  if (vote === 'A') return 'url(#pattern-option-a)';
  if (vote === 'B') return 'url(#pattern-option-b)';
  return DEFAULT_FILL;
}

function getMemeStroke(isoA3, mapData, memeOverrides) {
  const override = getMemeOverride(isoA3, memeOverrides, mapData.countryVotes);
  if (override === 'A') {
    return { stroke: mapData.optionA.color, strokeWidth: 2.5, strokeDasharray: '4 2' };
  }
  if (override === 'B') {
    return { stroke: mapData.optionB.color, strokeWidth: 2.5, strokeDasharray: '4 2' };
  }
  return { stroke: '#0f172a', strokeWidth: 0.5, strokeDasharray: 'none' };
}

export default function InteractiveVotingMap({
  mapData,
  onVoteCast,
  votingDisabled = false,
  isMemeMode = false,
  memeOverrides = {},
  onMemeOverride,
  hideMapHints = false,
}) {
  const [hoveredGeo, setHoveredGeo] = useState(null);

  const regionConfig = useMemo(
    () => getRegionConfig(mapData?.region),
    [mapData?.region]
  );

  const { geography, projection, zoomCenter } = regionConfig;

  const handleGeoClick = (geo) => {
    const isoA3 = getCountryId(geo);
    const voteKey = resolveVoteKey(isoA3, mapData.countryVotes);

    if (isMemeMode) {
      if (onMemeOverride) onMemeOverride(voteKey);
    } else if (onVoteCast && !votingDisabled) {
      onVoteCast(voteKey);
    }
  };

  const hoveredName = useMemo(() => {
    if (!hoveredGeo || !geography) return null;
    return hoveredGeo.name;
  }, [hoveredGeo, geography]);

  return (
    <div className="relative w-full h-full min-h-[400px] bg-slate-900 rounded-xl overflow-hidden border border-slate-800 shadow-2xl flex items-center justify-center">
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)',
          backgroundSize: '20px 20px',
        }}
      />

      <ComposableMap
        projection="geoMercator"
        projectionConfig={projection}
        className="w-full h-full z-10"
      >
        <MapPatternDefs optionA={mapData.optionA} optionB={mapData.optionB} />
        <ZoomableGroup zoom={1} minZoom={0.5} maxZoom={8} center={zoomCenter}>
          <Geographies geography={geography}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const isoA3 = getCountryId(geo);
                const fillPattern = getGeoFill(isoA3, mapData, { isMemeMode, memeOverrides });
                const memeStroke = isMemeMode ? getMemeStroke(isoA3, mapData, memeOverrides) : null;
                const isHovered = hoveredGeo?.id === isoA3;

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={() => setHoveredGeo({ id: isoA3, name: getCountryName(geo) })}
                    onMouseLeave={() => setHoveredGeo(null)}
                    onClick={() => handleGeoClick(geo)}
                    style={{
                      default: {
                        fill: fillPattern,
                        stroke: memeStroke?.stroke ?? '#0f172a',
                        strokeWidth: memeStroke?.strokeWidth ?? 0.5,
                        strokeDasharray: memeStroke?.strokeDasharray ?? 'none',
                        outline: 'none',
                      },
                      hover: {
                        fill: fillPattern,
                        stroke: isMemeMode && memeStroke?.strokeWidth > 1 ? memeStroke.stroke : '#ffffff',
                        strokeWidth: isMemeMode && memeStroke?.strokeWidth > 1 ? memeStroke.strokeWidth : 1.5,
                        strokeDasharray: memeStroke?.strokeDasharray ?? 'none',
                        outline: 'none',
                        cursor: votingDisabled ? 'not-allowed' : 'pointer',
                        opacity: votingDisabled ? 0.85 : 1,
                        filter: 'brightness(1.15)',
                      },
                      pressed: {
                        fill: fillPattern,
                        stroke: isMemeMode && memeStroke?.strokeWidth > 1 ? memeStroke.stroke : '#ffffff',
                        strokeWidth: isMemeMode && memeStroke?.strokeWidth > 1 ? memeStroke.strokeWidth : 1.5,
                        strokeDasharray: memeStroke?.strokeDasharray ?? 'none',
                        outline: 'none',
                      },
                    }}
                    className={
                      isHovered
                        ? 'stroke-white stroke-[1.5] [filter:drop-shadow(0_0_6px_rgba(255,255,255,0.4))]'
                        : ''
                    }
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {hoveredName && !isMemeMode && (
        <div className="absolute bottom-4 left-4 bg-slate-950/90 backdrop-blur border border-slate-800 p-3 rounded shadow-xl z-20 pointer-events-none">
          <p className="font-bold text-lg">{hoveredName}</p>
          <div className="flex items-center gap-2 mt-1">
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: mapData.optionA.color }}
            />
            <span className="text-sm text-slate-300">vs</span>
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: mapData.optionB.color }}
            />
          </div>
        </div>
      )}

      {!hideMapHints && (
        <div className="export-exclude absolute bottom-4 right-4 bg-slate-950/80 backdrop-blur px-3 py-1.5 rounded-md text-[10px] text-slate-400 font-bold shadow-sm uppercase tracking-tighter z-20 pointer-events-none">
          Scroll to zoom • Drag to pan
        </div>
      )}
    </div>
  );
}
